import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@shared/components/Table';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import useFetchData from '@shared/hooks/useFetchData';
import { excessByEmployeeService } from '../services/excessByEmployee';
import { useForm } from 'react-hook-form';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import useModal from '@shared/hooks/useModal';
import { useStatusBadge } from '@shared/components/StatusBadge';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';
import ExcessByEmployeeForm from './ExcessByEmployeeForm';
import ExcessByEmployeeTranslation from './ExcessByEmployeeTranslation';
import DeleteModal from '@shared/components/DeleteModal';

export default function ExcessByEmployee() {
    const { t } = useTranslation();
    const { pageNumber, fetchData, pageSize, sortBy, sortDir, data, loading, error, totalPages, totalElements, setpageNumber, setPageSize, searchQuery, setSearchQuery, handleSearchClick, setSortBy, setSortDir } = useFetchData();
    const [selectedActive, setSelectedActive] = useState('true');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const { statusBadge } = useStatusBadge();

    const formProps = useForm({
        defaultValues: {
            code: '',
            reason: '',
            kindOfCompensation: '',
            active: true
        }
    });

    useEffect(() => {
        const params = {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
        };
        
        // Add active filter if selectedActive is set
        if (selectedActive !== null && selectedActive !== '') {
            params.active = selectedActive === 'true';
        }
        
        fetchData(excessByEmployeeService.getExcessByEmployees, params);
    }, [pageNumber, pageSize, sortBy, sortDir, selectedActive]);

    // Columns configuration
    const columns = [
        {
            key: 'code',
            label: t('staff.excessByEmployee.table.headers.code'),
        },
        {
            key: 'reason',
            label: t('staff.excessByEmployee.table.headers.reason'),
        },
        {
            key: 'kindOfCompensation',
            label: t('staff.excessByEmployee.table.headers.kindOfCompensation'),
        },
        {
            key: 'active',
            label: t('staff.excessByEmployee.table.headers.active'),
        },
        {
            key: 'translated',
            label: t('staff.excessByEmployee.table.headers.translated'),
        }
    ];

    // Search options for reason and kindOfCompensation
    const searchOptions = [
        { key: 'reason', label: 'reason' },
        { key: 'kindOfCompensation', label: 'kindOfCompensation' }
    ];

    // Report generator for excess by employee
    const { 
        generatePdfReport, 
        generateExcelReport,
        pdfLoading,
        excelLoading
    } = useReportGenerator({
        title: t('staff.excessByEmployee.title'),
        columns: [
            { key: 'code', label: t('staff.excessByEmployee.table.headers.code') },
            { key: 'reason', label: t('staff.excessByEmployee.table.headers.reason') },
            { key: 'kindOfCompensation', label: t('staff.excessByEmployee.table.headers.kindOfCompensation') },
            { key: 'active', label: t('staff.excessByEmployee.table.headers.active') },
            { key: 'translated', label: t('staff.excessByEmployee.table.headers.translated') }
        ],
        fileName: 'excess_by_employee_report',
        companyName: '',
        reportService: () => {
            const params = { searchQuery };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            return excessByEmployeeService.getExcessByEmployeeReportData(params);
        },
        customStyles: {}
    });

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleSort = (column) => {
        let newSortDir;
        if (sortBy === column) {
            newSortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            newSortDir = 'asc';
            setSortBy(column);
        }
        setSortDir(newSortDir);
        setpageNumber(0);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit' && selectedRecord) {
                await excessByEmployeeService.updateExcessByEmployee(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await excessByEmployeeService.createExcessByEmployee(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            const params = {
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
            };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            fetchData(excessByEmployeeService.getExcessByEmployees, params);
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleConfirmDelete = useDeleteHandler({
        service: excessByEmployeeService,
        entityName: 'ExcessByEmployee',
        selectedData: selectedRecord,
        fetchData,
        setSelectedData: setSelectedRecord,
        handleCloseModal,
        customFetchParams: {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            ...(selectedActive !== null && selectedActive !== '' && { active: selectedActive === 'true' }),
        },
        successMessage: t('common.deletedSuccessfully'),
        errorMessage: t('common.errorMessage'),
    });

    // Action buttons
    const actionButtons = (
        <ActionButtons
            buttons={[{
                ...COMMON_BUTTONS.ADD,
                onClick: () => handleOpenModal('add')
            },
            {
                ...COMMON_BUTTONS.EDIT,
                onClick: () => handleOpenModal('edit')
            },
            {
                ...COMMON_BUTTONS.TRANSLATE,
                onClick: () => handleOpenModal('translate')
            },
            {
                ...COMMON_BUTTONS.DELETE,
                onClick: () => handleOpenModal('delete')
            },
            {
                ...COMMON_BUTTONS.DOWNLOAD_PDF,
                onClick: generatePdfReport,
                disabled: pdfLoading,
                icon: pdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                iconProps: pdfLoading ? { spin: true } : {},
                title: pdfLoading ? "Генериране на PDF..." : "common.downloadPDF",
                showWhen: 'always'
            },
            {
                ...COMMON_BUTTONS.EXCEL,
                onClick: generateExcelReport,
                disabled: excelLoading,
                icon: excelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                iconProps: excelLoading ? { spin: true } : {},
                title: excelLoading ? "Генериране на Excel..." : "common.downloadExcel",
                showWhen: 'always'
            }
            ]}
            selectedRecord={selectedRecord}
        />
    );

    return (
        <>
            <Table
                title={t('staff.excessByEmployee.title')}
                actionButtons={actionButtons}
                columns={columns}
                data={data}
                loading={loading}
                error={error}
                selectedData={selectedRecord?.id}
                handleSearchClick={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    handleSearchClick(excessByEmployeeService.getExcessByEmployees, params);
                }}
                getData={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    fetchData(excessByEmployeeService.getExcessByEmployees, params);
                }}
                handleCheckboxChange={(index) => handleSelectRecord(data[index])}
                pageNumber={pageNumber}
                showPageSizeSelector={false}
                showPagination={true}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setpageNumber={setpageNumber}
                setPageSize={setPageSize}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                renderCell={(item, column) => statusBadge(item, column)}
                sortBy={sortBy}
                sortDir={sortDir}
                handleSort={handleSort}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.reason || selectedRecord?.code}
                    entityType="record"
                />
            )}

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <ExcessByEmployeeForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'translate' && (
                <ExcessByEmployeeTranslation
                    onClose={handleCloseModal}
                    record={selectedRecord}
                    onRefresh={() => {
                        const params = {
                            pageNumber,
                            pageSize,
                            sortBy,
                            sortDir,
                            searchQuery,
                        };
                        if (selectedActive !== null && selectedActive !== '') {
                            params.active = selectedActive === 'true';
                        }
                        return fetchData(excessByEmployeeService.getExcessByEmployees, params);
                    }}
                />
            )}
        </>
    );
}

